import React from "react";
import { PLAN_PRICING, fmtINR } from "@/lib/plans";
const GROWTH = PLAN_PRICING.find(p => p.key === "growth");
import { Link } from "react-router-dom";
import { X, TrendingUp, ArrowRight, Lock } from "lucide-react";

/**
 * UpgradePlanModal — shown when backend returns 403 plan-limit error.
 * Props:
 *   message  – the detail string from the 403 response
 *   onClose  – callback to dismiss
 */
export default function UpgradePlanModal({ message, onClose }) {
  if (!message) return null;

  // Extract plan name from message, e.g. "... Your Starter plan allows ..."
  const planMatch = message.match(/Your (.+?) plan/i);
  const currentPlan = planMatch?.[1] || "current";

  const isProject = message.toLowerCase().includes("project");
  const entity = isProject ? "projects" : "members";
  const Icon = isProject ? Lock : Lock;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="animate-scale-in w-full max-w-sm"
        style={{
          background: "#FFFFFF",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "20px",
          padding: "28px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Icon + Close */}
        <div className="flex items-start justify-between mb-5">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: "rgba(59,130,246,0.12)",
              border: "1px solid rgba(59,130,246,0.25)",
            }}
          >
            <TrendingUp size={20} style={{ color: "#60A5FA" }} />
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.3)", cursor: "pointer", padding: "4px" }}
            onMouseEnter={e => { e.currentTarget.style.color = "rgba(255,255,255,0.8)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.3)"; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <h3
          className="font-heading font-bold text-white mb-2"
          style={{ fontSize: "17px", letterSpacing: "-0.02em" }}
        >
          {isProject ? "Project Limit Reached" : "Member Limit Reached"}
        </h3>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", lineHeight: "1.65", marginBottom: "20px" }}>
          Your <span style={{ color: "#60A5FA", fontWeight: "600" }}>{currentPlan}</span> plan has reached
          its {entity} limit. Upgrade to add more {entity} and keep growing.
        </p>

        {/* Plan comparison hint */}
        <div
          style={{
            background: "rgba(59,130,246,0.07)",
            border: "1px solid rgba(59,130,246,0.18)",
            borderRadius: "12px",
            padding: "12px 14px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "4px" }}>Growth Plan</div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.75)" }}>
              {GROWTH.projects} projects · {GROWTH.users} members · {GROWTH.storage_gb} GB storage
            </div>
          </div>
          <div style={{ fontSize: "13px", fontWeight: "800", color: "#60A5FA", fontFamily: "Space Grotesk" }}>{fmtINR(GROWTH.price)}<span style={{ fontSize: "10px", fontWeight: "400", color: "rgba(255,255,255,0.3)" }}>/mo</span></div>
        </div>

        {/* CTAs */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "10px 0", borderRadius: "10px", fontSize: "13px", fontWeight: "600",
              background: "rgba(46,100,68,0.08)", border: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.4)", cursor: "pointer", transition: "background 0.2s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(46,100,68,0.08)"; }}
          >
            Maybe Later
          </button>
          <Link
            to="/app/billing"
            onClick={onClose}
            data-testid="upgrade-plan-cta"
            style={{
              flex: 2, padding: "10px 0", borderRadius: "10px", fontSize: "13px", fontWeight: "700",
              background: "linear-gradient(135deg, #3B82F6, #6366F1)", color: "#1B2B22",
              textDecoration: "none", textAlign: "center", display: "flex",
              alignItems: "center", justifyContent: "center", gap: "6px",
              boxShadow: "0 0 16px rgba(59,130,246,0.35)", transition: "box-shadow 0.2s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 28px rgba(59,130,246,0.55)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 16px rgba(59,130,246,0.35)"; }}
          >
            Upgrade Plan <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
